import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface QRScannerProps {
  onDecoded: (qrCode: string) => void;
  onCancel: () => void;
  /** Header title. Defaults to "Ler QR". */
  title?: string;
  /** Helper copy shown under the viewport while the camera is running. */
  instructions?: string;
  /** Helper copy shown on the prompt screen before the camera starts. */
  promptHint?: string;
  /** Dialog aria-label. Defaults to `title`. */
  ariaLabel?: string;
}

const READER_ID = 'qr-reader';
const GRANTED_HINT_KEY = 'pwa.camera.previouslyGranted';
const DEFAULT_TITLE = 'Ler QR';
const DEFAULT_INSTRUCTIONS = 'Aponte a câmara para o código QR.';
const DEFAULT_PROMPT_HINT = 'Toque para iniciar a câmara e ler o código QR.';

type PermissionStage = 'probing' | 'prompt' | 'starting' | 'running' | 'denied';

/**
 * Browser/getUserMedia failures arrive as DOMException-like errors with English
 * `name`/`message` fields. Translate the common ones to pt-PT so the raw browser
 * string never reaches the UI.
 */
function cameraErrorMessage(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'O acesso à câmara está bloqueado. Active a câmara nas definições do dispositivo.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Não foi encontrada nenhuma câmara neste dispositivo.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'A câmara está a ser utilizada por outra aplicação.';
  }
  return 'Não foi possível aceder à câmara. Tente novamente.';
}

type QueryablePermissionName = PermissionName | 'camera';

interface PermissionsWithCamera {
  query(descriptor: { name: QueryablePermissionName }): Promise<PermissionStatus>;
}

/**
 * Full-screen camera scanner reused across QR flows (Centro check-in, equipment
 * reservation, …). The caller decides the copy via `title` / `instructions` /
 * `promptHint`; the camera/permission lifecycle is shared.
 *
 * On iOS Safari the OS-level camera prompt cannot be fully suppressed across
 * PWA sessions, but we can avoid surprise re-prompts within a session by:
 *   1. Probing `navigator.permissions.query` (when available) before touching getUserMedia.
 *   2. Auto-starting silently when permission is already `granted`.
 *   3. Showing an explicit "Iniciar câmara" tap target on `prompt` so the OS
 *      dialog fires from a user gesture rather than on every mount.
 * A `previouslyGranted` localStorage hint biases toward auto-start on older
 * iOS where the Permissions API is unreliable.
 *
 * The camera lifecycle is intentionally driven imperatively (via refs) rather
 * than by an effect keyed on `stage`. An earlier version keyed the start effect
 * on `[stage]`, which meant `setStage('running')` after a successful start
 * triggered the effect's cleanup and immediately stopped the camera. Keep the
 * start/stop teardown bound to mount/unmount so stage transitions stay UI-only.
 */
export default function QRScanner({
  onDecoded,
  onCancel,
  title = DEFAULT_TITLE,
  instructions = DEFAULT_INSTRUCTIONS,
  promptHint = DEFAULT_PROMPT_HINT,
  ariaLabel,
}: QRScannerProps) {
  const instanceRef = useRef<Html5Qrcode | null>(null);
  const handledRef = useRef(false);
  const hasStartedRef = useRef(false);
  const onDecodedRef = useRef(onDecoded);
  const startScannerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onDecodedRef.current = onDecoded;
  }, [onDecoded]);

  const initialStageRef = useRef<PermissionStage>(readPreviouslyGranted() ? 'starting' : 'probing');
  const [stage, setStage] = useState<PermissionStage>(initialStageRef.current);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const startScanner = () => {
      if (cancelled || hasStartedRef.current) return;
      hasStartedRef.current = true;
      setStage('starting');

      const instance = new Html5Qrcode(READER_ID, { verbose: false });
      instanceRef.current = instance;

      instance
        .start(
          { facingMode: 'environment' },
          // No `qrbox`: passing one makes html5-qrcode draw its own white
          // shaded-region box, which is centred on the video frame and drifts
          // out of alignment with our branded green overlay (centred on the
          // container) whenever the feed is letterboxed. Scanning the full
          // viewfinder instead leaves a single, aligned guide on screen.
          { fps: 10 },
          (decodedText) => {
            if (handledRef.current) return;
            handledRef.current = true;
            onDecodedRef.current(decodedText);
          },
          () => {
            // Per-frame decode misses fire constantly; ignore.
          },
        )
        .then(() => {
          if (cancelled) return;
          rememberCameraGranted();
          setStage('running');
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          hasStartedRef.current = false;
          instanceRef.current = null;
          setError(cameraErrorMessage(err));
          setStage('denied');
        });
    };

    startScannerRef.current = startScanner;

    if (initialStageRef.current === 'starting') {
      startScanner();
    } else {
      probeCameraPermission()
        .then((result) => {
          if (cancelled) return;
          if (result === 'granted') {
            startScanner();
          } else if (result === 'denied') {
            setError('O acesso à câmara está bloqueado. Active a câmara nas definições do dispositivo.');
            setStage('denied');
          } else {
            setStage('prompt');
          }
        })
        .catch(() => {
          if (cancelled) return;
          // Permissions API not supported or threw — fall back to explicit tap.
          setStage('prompt');
        });
    }

    return () => {
      cancelled = true;
      startScannerRef.current = null;
      const live = instanceRef.current;
      instanceRef.current = null;
      if (!live) return;
      live
        .stop()
        .catch(() => {
          // Stopping a never-started scanner is fine.
        })
        .finally(() => live.clear());
    };
  }, []);

  const showOverlay = stage === 'running';

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#16101f] text-white"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
    >
      <div className="flex items-center justify-between px-5 pt-6 pb-4">
        <h2 className="text-base font-bold">{title}</h2>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold hover:bg-white/20"
        >
          Cancelar
        </button>
      </div>

      <div className="relative flex-1">
        <div id={READER_ID} className="absolute inset-0" />

        {showOverlay && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-60 w-60 rounded-3xl border-2 border-[#4ab065]/70">
              <span className="absolute -top-1 -left-1 h-8 w-8 rounded-tl-md border-t-4 border-l-4 border-[#4ab065]" />
              <span className="absolute -top-1 -right-1 h-8 w-8 rounded-tr-md border-t-4 border-r-4 border-[#4ab065]" />
              <span className="absolute -bottom-1 -left-1 h-8 w-8 rounded-bl-md border-b-4 border-l-4 border-[#4ab065]" />
              <span className="absolute -bottom-1 -right-1 h-8 w-8 rounded-br-md border-b-4 border-r-4 border-[#4ab065]" />
              <span className="absolute inset-x-4 top-1/2 h-0.5 -translate-y-1/2 bg-[#8b6dd1] shadow-[0_0_12px_#8b6dd1]" />
            </div>
          </div>
        )}

        {stage === 'prompt' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <p className="text-base font-semibold">A câmara está pronta a abrir</p>
            <p className="text-sm text-white/70">{promptHint}</p>
            <button
              type="button"
              onClick={() => startScannerRef.current?.()}
              className="rounded-xl bg-[#4ab065] px-5 py-2.5 text-sm font-bold text-white shadow"
            >
              Iniciar câmara
            </button>
          </div>
        )}

        {stage === 'probing' && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
            <p className="text-sm text-white/70">A preparar a câmara…</p>
          </div>
        )}

        {stage === 'denied' && error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <p className="text-base font-semibold">Câmara indisponível</p>
            <p className="text-sm text-white/70">{error}</p>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl bg-[#4ab065] px-5 py-2.5 text-sm font-bold text-white shadow"
            >
              Fechar
            </button>
          </div>
        )}
      </div>

      {showOverlay && (
        <p className="px-6 pb-8 pt-4 text-center text-sm text-white/80">{instructions}</p>
      )}
    </div>
  );
}

async function probeCameraPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (typeof navigator === 'undefined' || !navigator.permissions) {
    return 'prompt';
  }
  const permissions = navigator.permissions as unknown as PermissionsWithCamera;
  const status = await permissions.query({ name: 'camera' });
  return status.state;
}

function readPreviouslyGranted(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(GRANTED_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberCameraGranted(): void {
  try {
    window.localStorage?.setItem(GRANTED_HINT_KEY, '1');
  } catch {
    // Storage may be unavailable (private mode, quota); the hint is best-effort.
  }
}
